import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { MessageSquareText, X, Send, Loader2 } from "lucide-react";
import { API } from "../lib/api";

const SID_KEY = "lv_chat_session";
const MSG_KEY = "lv_chat_messages";
const WELCOME = { role: "assistant", content: "Bonjour ! Je suis l'assistant du Véridique. Posez-moi vos questions sur le journal, nos rubriques, l'infolettre ou comment nous contacter." };
const SUGGESTIONS = ["Le site est-il gratuit ?", "Comment signaler une erreur ?", "Comment vous contacter ?"];

const getSid = () => {
  let sid = localStorage.getItem(SID_KEY);
  if (!sid) { sid = `lv_${crypto.randomUUID()}`; localStorage.setItem(SID_KEY, sid); }
  return sid;
};

async function streamChat(message, onToken) {
  const res = await fetch(`${API}/chat/stream`, {
    method: "POST", headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ session_id: getSid(), message }),
  });
  if (!res.ok) throw new Error(res.status === 429 ? "Trop de questions, réessayez plus tard." : "Assistant indisponible.");
  const reader = res.body.getReader();
  const dec = new TextDecoder();
  let buf = "";
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buf += dec.decode(value, { stream: true });
    const parts = buf.split("\n\n");
    buf = parts.pop();
    for (const p of parts) {
      const data = p.replace(/^data: /, "");
      if (data === "[DONE]") return;
      const ev = JSON.parse(data);
      if (ev.error) throw new Error(ev.error);
      onToken(ev.t);
    }
  }
}

function Bubble({ m, i }) {
  const mine = m.role === "user";
  return (
    <div className={`flex ${mine ? "justify-end" : "justify-start"}`}>
      <div data-testid={`chat-message-${i}`}
        className={`max-w-[85%] px-4 py-2.5 rounded-2xl font-sub text-sm leading-relaxed whitespace-pre-wrap ${mine ? "bg-[#E11D48] text-white rounded-br-sm" : "bg-white text-[#0A0A0A] border border-[#E2E8F0] rounded-bl-sm"}`}>
        {m.content || <Loader2 size={14} className="animate-spin text-slate-400" />}
      </div>
    </div>
  );
}

export default function ChatBubble() {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [msgs, setMsgs] = useState(() => JSON.parse(localStorage.getItem(MSG_KEY) || "null") || [WELCOME]);
  const endRef = useRef(null);

  useEffect(() => { localStorage.setItem(MSG_KEY, JSON.stringify(msgs.slice(-40))); endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);

  if (pathname.startsWith("/admin")) return null;

  const ask = async (text) => {
    const q = text.trim();
    if (!q || busy) return;
    setInput("");
    setBusy(true);
    setMsgs((m) => [...m, { role: "user", content: q }, { role: "assistant", content: "" }]);
    const patch = (fn) => setMsgs((m) => { const c = [...m]; c[c.length - 1] = { role: "assistant", content: fn(c[c.length - 1].content) }; return c; });
    try {
      await streamChat(q, (t) => patch((prev) => prev + t));
    } catch (e) {
      patch(() => e.message || "Assistant indisponible.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      {open && (
        <div data-testid="chat-panel"
          className="fixed z-50 bottom-24 right-4 left-4 sm:left-auto sm:w-[380px] h-[min(560px,70vh)] bg-[#FAFAF8] rounded-2xl shadow-2xl border border-[#E2E8F0] flex flex-col overflow-hidden animate-in fade-in slide-in-from-bottom-4 duration-200">
          <div className="bg-[#0F172A] text-white px-5 py-4 flex items-center justify-between">
            <div>
              <p className="font-headline font-black">Assistant <span className="text-[#E11D48]">Véridique</span></p>
              <p className="meta-label text-slate-400">Questions sur le journal</p>
            </div>
            <button onClick={() => setOpen(false)} data-testid="chat-close-button" aria-label="Fermer" className="w-8 h-8 rounded-full hover:bg-white/10 flex items-center justify-center"><X size={18} /></button>
          </div>
          <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="chat-messages">
            {msgs.map((m, i) => <Bubble key={i} m={m} i={i} />)}
            {msgs.length === 1 && (
              <div className="flex flex-wrap gap-2 pt-1">
                {SUGGESTIONS.map((s, i) => (
                  <button key={s} onClick={() => ask(s)} data-testid={`chat-suggestion-${i}`} className="font-sub text-xs border border-[#E11D48]/40 text-[#E11D48] rounded-full px-3 py-1.5 hover:bg-[#E11D48] hover:text-white transition-colors">{s}</button>
                ))}
              </div>
            )}
            <div ref={endRef} />
          </div>
          <form onSubmit={(e) => { e.preventDefault(); ask(input); }} className="p-3 border-t border-[#E2E8F0] bg-white flex gap-2">
            <input value={input} onChange={(e) => setInput(e.target.value)} maxLength={1000} placeholder="Votre question…" data-testid="chat-input"
              className="flex-1 bg-[#FAFAF8] rounded-full px-4 py-2.5 font-sub text-sm outline-none focus:ring-1 focus:ring-[#E11D48]" />
            <button type="submit" disabled={busy} data-testid="chat-send-button" aria-label="Envoyer" className="w-10 h-10 rounded-full bg-[#E11D48] text-white flex items-center justify-center disabled:opacity-50">
              {busy ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
            </button>
          </form>
        </div>
      )}
      <button onClick={() => setOpen((o) => !o)} data-testid="chat-bubble-button" aria-label="Assistant"
        className="fixed z-50 bottom-5 right-4 w-14 h-14 rounded-full bg-[#E11D48] text-white shadow-xl flex items-center justify-center hover:scale-105 active:scale-95 transition-transform duration-200">
        {open ? <X size={22} /> : <MessageSquareText size={22} />}
      </button>
    </>
  );
}
